import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle, Ruler, FilePdf, Users,
  Lightning, Crown, MapPin, Calculator, Star, ShieldCheck,
  SpinnerGap, Stack, Gauge, Clock, Path, Camera, Leaf
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { SEO } from '../components/SEO';
import { HiTexProductsFooter } from '../components/HiTexProductsFooter';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

// Outcome-first benefits. No prices, no competitor math — just what changes for YOU.
const OUTCOMES = [
  {
    icon: Clock,
    eyebrow: 'Speed',
    title: 'Quote in the driveway, not the office.',
    body: 'Pull up, satellite-lock the property, and hand over a signed quote before the homeowner refills their coffee. No late-night bid math.',
  },
  {
    icon: Ruler,
    eyebrow: 'Accuracy',
    title: 'Exact square footage, every zone, every time.',
    body: 'Draw polygons over lawn, beds, hardscape, and driveway. The math is done for you — cubic yards, linear feet, acres — so you stop eating bad estimates.',
  },
  {
    icon: Lightning,
    eyebrow: 'Effortless',
    title: 'Let the AI map it while you prep the pitch.',
    body: 'One tap. The satellite scan auto-detects lawn, house, beds, trees, and driveway — with condition notes — in about eight seconds.',
  },
  {
    icon: FilePdf,
    eyebrow: 'Polish',
    title: 'Quotes that close themselves.',
    body: 'Clean, branded PDFs with the TurfPulse watermark and your contact block. Customers sign faster when the quote looks like a pro built it — because one did.',
  },
  {
    icon: Users,
    eyebrow: 'Scale',
    title: 'All your clients in one place.',
    body: 'Save properties, revisit last season\'s zones, reuse measurements for follow-up services. No more hunting through spreadsheets or text threads.',
  },
  {
    icon: Stack,
    eyebrow: 'All-in-one',
    title: 'Turf science + business tools, unified.',
    body: 'The same GDD tracking, soil analysis, spray forecasts, and product knowledge powering 300+ lawns — bundled with the tools you actually bill from.',
  },
];

const WORKFLOW = [
  { icon: MapPin, title: 'Drive up. Open the app.', body: 'Type the address. Satellite view locks on the property in 2 seconds.' },
  { icon: Lightning, title: 'Tap AI Scan.', body: 'Zones auto-detected — lawn, hardscape, beds, driveway — with square footage.' },
  { icon: Calculator, title: 'Pick the service.', body: 'Leveling, sod, aeration, overseed, mowing, custom. Materials and labor calculate themselves.' },
  { icon: FilePdf, title: 'Hand over the quote.', body: 'Branded PDF drops straight to your phone. They sign, you book the job.' },
];

const PROMISES = [
  { icon: Gauge, label: 'Quote faster' },
  { icon: Camera, label: 'Measure without walking' },
  { icon: Leaf, label: 'Back every bid with turf science' },
  { icon: ShieldCheck, label: 'Look pro every time' },
];

const ProLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);

  const isPro = user?.role === 'admin' || user?.subscription_tier === 'pro_business';

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/?plan=pro');
      return;
    }
    if (isPro) {
      navigate('/measure');
      return;
    }
    setCheckingOut(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/payments/subscribe`,
        { tier: 'pro_business' },
        { withCredentials: true }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not open checkout — try again.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Checkout error';
      toast.error(String(detail));
    } finally {
      setCheckingOut(false);
    }
  };

  const primaryCtaLabel = checkingOut
    ? 'Opening checkout…'
    : isPro ? 'Open Property Measure' : 'Unlock the Pro toolkit';

  return (
    <div className="min-h-screen bg-background" data-testid="pro-landing-page">
      <SEO
        title="TurfPulse Pro — Measure. Quote. Win the Job."
        description="The all-in-one field toolkit for lawn care pros. Satellite property measuring, AI zone detection, branded quote PDFs, and the turf science to back every bid."
      />

      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="pro-logo-link">
            <TurfPulseLogoSVG size={28} />
            <span className="text-sm font-semibold tracking-tight">TurfPulse <span className="text-primary">Pro</span></span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1" data-testid="pro-back-btn">
            <ArrowLeft size={14} /> Back
          </Button>
        </div>
      </header>

      {/* Hero — outcome-first, no price */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest mb-5">
              <Crown size={12} weight="fill" /> For Contractors & Turf Pros
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
              Measure any lawn.<br />
              Quote it in 60 seconds.<br />
              <span className="text-primary">Win the job before the next truck pulls in.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
              TurfPulse Pro is the all-in-one field toolkit every lawn care business wishes it had. Satellite measuring, AI zone detection, branded quotes, and the turf science to back every bid — on one screen, in your truck.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={handleSubscribe}
                disabled={checkingOut}
                className="px-8 py-6 text-base font-bold gap-2"
                data-testid="pro-subscribe-btn"
              >
                {checkingOut ? <SpinnerGap size={18} className="animate-spin" /> : null}
                {primaryCtaLabel}
                {!checkingOut && <ArrowRight size={18} weight="bold" />}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('outcomes')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-6 text-base"
                data-testid="pro-features-scroll"
              >
                See what it unlocks
              </Button>
            </div>

            {/* Promise row — outcomes, never price */}
            <div className="flex items-center gap-4 sm:gap-6 mt-6 text-xs text-muted-foreground flex-wrap">
              {PROMISES.map((p) => {
                const Icon = p.icon;
                return (
                  <span key={p.label} className="flex items-center gap-1.5">
                    <Icon size={14} weight="fill" className="text-emerald-500" /> {p.label}
                  </span>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outcomes Grid */}
      <section id="outcomes" className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">What changes for you</p>
            <h2 className="text-3xl sm:text-4xl font-bold">Faster bids. Sharper quotes.<br />More closed jobs.</h2>
            <p className="text-muted-foreground mt-3">Pro was built around one idea: your truck is your office, and your phone should do the heavy lifting.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Card className="h-full border border-border/60 hover:border-primary/40 hover:shadow-md transition-all">
                    <CardContent className="p-6">
                      <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon size={22} weight="duotone" className="text-primary" />
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-primary/80 mb-1">{o.eyebrow}</p>
                      <h3 className="text-base font-bold mb-2">{o.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{o.body}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* All-in-one Belt */}
      <section className="py-14 px-4 sm:px-6 bg-muted/30 border-y border-border/40">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">One app. The whole job.</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Stop juggling four apps to close one bid.</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            Measuring tool. Quoting tool. Weather. GDD. Soil analysis. Spray forecasts. Product recommendations. Client history. It&apos;s all the same screen — so you pull up, scan, quote, and drive to the next lead without switching tabs.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {[
              'Property Measure', 'AI Zone Scan', 'Branded Quotes',
              'GDD Tracking', 'Soil Analysis', 'Spray Forecast',
              'Photo Diagnosis', 'Product Library', 'Client History',
            ].map((tag) => (
              <span
                key={tag}
                className="text-[11px] px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground"
                data-testid={`pro-tool-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Day-in-the-life Workflow */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">A day with Pro</p>
            <h2 className="text-3xl sm:text-4xl font-bold">Pull up. Scan. Quote. Book it.</h2>
            <p className="text-muted-foreground mt-3">Four taps from the curb to a signed estimate. No clipboard, no measuring wheel, no guesswork.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {WORKFLOW.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="relative flex gap-4 p-5 rounded-xl border border-border/60 bg-card"
                  data-testid={`pro-step-${i}`}
                >
                  <div className="shrink-0 w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon size={22} weight="duotone" className="text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Step {String(i + 1).padStart(2, '0')}</p>
                    <h4 className="text-base font-bold mb-1">{s.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Promise / Value framing — still no price */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-primary/5 via-transparent to-accent/5">
        <div className="max-w-3xl mx-auto text-center">
          <Path size={44} weight="duotone" className="mx-auto text-primary mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Your yard. Your crew. Your call.</h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
            TurfPulse Pro isn&apos;t just software. It&apos;s a shortcut to looking like the most prepared contractor on the block — every time you pull into a driveway. Whether you service five homes or fifty, the toolkit scales with the work, not the billing.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-left">
            <div className="rounded-lg bg-card border border-border/60 p-4">
              <Star size={18} weight="fill" className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">Look credible</p>
              <p className="text-xs text-muted-foreground">Branded quotes, science-backed recommendations, satellite proof of work area.</p>
            </div>
            <div className="rounded-lg bg-card border border-border/60 p-4">
              <CheckCircle size={18} weight="fill" className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">Close faster</p>
              <p className="text-xs text-muted-foreground">Quote in the driveway. Signed estimates before the homeowner has time to call a competitor.</p>
            </div>
            <div className="rounded-lg bg-card border border-border/60 p-4">
              <Lightning size={18} weight="fill" className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">Work smarter</p>
              <p className="text-xs text-muted-foreground">Stop measuring by foot. Stop eyeballing areas. Let the app do it — accurately — every time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — price only in disclosure, never in the headline */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="border-2 border-primary/30 shadow-lg">
            <CardContent className="p-8 sm:p-12 text-center space-y-5">
              <Crown size={44} weight="duotone" className="mx-auto text-primary" />
              <h3 className="text-3xl sm:text-4xl font-bold">Run your business like the pros do.</h3>
              <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
                Every contractor tool, every measurement, every quote, every client — together in one app, ready whenever the next lead calls.
              </p>

              <Button
                size="lg"
                onClick={handleSubscribe}
                disabled={checkingOut}
                className="w-full sm:w-auto px-10 py-6 text-base font-bold gap-2 mt-2"
                data-testid="pro-final-cta-btn"
              >
                {checkingOut ? (
                  <><SpinnerGap size={18} className="animate-spin" /> Opening checkout…</>
                ) : isPro ? (
                  <>Open Property Measure <ArrowRight size={18} weight="bold" /></>
                ) : (
                  <>Activate Pro <ArrowRight size={18} weight="bold" /></>
                )}
              </Button>

              {/* Price appears in disclosure only — required for transparency, de-emphasized per brand strategy */}
              <p className="text-[11px] text-muted-foreground">
                Annual subscription · Cancel anytime · Secure checkout by Stripe
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Not a contractor?{' '}
            <Link to="/" className="text-primary hover:underline" data-testid="pro-back-home-link">
              See what TurfPulse does for homeowners →
            </Link>
          </p>
        </div>
      </section>

      <footer className="border-t border-border/60 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-[11px] text-muted-foreground space-y-3">
          <div>
            Powered by TurfPulse™ — Smart Lawn Care, Backed by Science
            <div className="flex items-center justify-center gap-3 mt-2">
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
              <span>·</span>
              <Link to="/" className="hover:text-foreground">Home</Link>
            </div>
          </div>
          <HiTexProductsFooter />
        </div>
      </footer>
    </div>
  );
};

export default ProLandingPage;
